import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
// facNumber is not provided as a string in AffectedStaffContact type; omit phone formatting
import { useMemo } from 'react'

import { getAffectedStaffContacts } from '../../../../../api/affected-staffs/get-affected-staff-contact'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { StaffMinimal } from '../../../../../lib/types/models/staff/staff'
import { StaffRequestSimple } from '../../../../../lib/types/models/staff-request/staff-request'
import { formatPhoneNumber } from '../../../../../utils/functions/format-phone-number'

type GroupedStaff = {
  month: string // e.g. "2025-08"
  facStaffs: Array<{
    id: number
    staff: StaffMinimal
    facNumber?: string
    staffRequest: StaffRequestSimple
  }>
}

type Props = {
  className: string
  affectedStaffId: number
}

const AffectedStaffUrgentNumberModal = ({ className, affectedStaffId }: Props) => {
  const { t, currentLanguage } = useTranslate()

  const { data, isPending } = useQuery({
    queryKey: ['facParisAffectedStaff', affectedStaffId],
    queryFn: () => getAffectedStaffContacts(affectedStaffId)
  })

  // Group the staff data by month based on the staffRequest startDate
  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return []

    const groups: Record<string, GroupedStaff> = data.reduce(
      (acc, staff) => {
        const startDate = staff.staffRequest?.startDate
        if (!startDate) return acc

        const d = new Date(startDate)
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (!acc[monthKey]) acc[monthKey] = { month: monthKey, facStaffs: [] }

        acc[monthKey].facStaffs.push({
          id: staff.id,
          staff: staff.staff,
          facNumber: staff.facNumber,
          staffRequest: staff.staffRequest
        })

        return acc
      },
      {} as Record<string, GroupedStaff>
    )

    // Convert to array and sort by year then month
    return Object.values(groups).sort((a, b) => {
      const [ay, am] = a.month.split('-').map((v) => parseInt(v, 10))
      const [by, bm] = b.month.split('-').map((v) => parseInt(v, 10))
      if (ay !== by) return ay - by
      return am - bm
    })
  }, [data])

  // Function to format month based on language
  const formatMonthDisplay = (monthString: string) => {
    // monthString is in format "YYYY-MM"
    const [year, month] = monthString.split('-')
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)

    return new Intl.DateTimeFormat(currentLanguage, {
      month: 'long'
    })
      .format(date)
      .replace(/^\w/, (c) => c.toUpperCase()) // Capitalize the first letter
  }

  if (isPending) return <Loader center />

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <InnerBox className="rounded-lg p-3 bg-white-hover">
        <div className="flex items-center gap-2.5 mobile:flex-col mobile:items-start">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            {t(
              'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.office.number'
            )}
          </Typography>
          <Badge variant="blue" text="+33 (0)1 42 67 20 00" />
        </div>
        <div className="h-2" />
        <div className="flex items-center gap-1 mobile:flex-col mobile:items-start">
          <Typography variant="secondary-body" color={tailwindColors.grey.lighter}>
            {t(
              'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.french.hour'
            )}{' '}
            :
          </Typography>
          <Badge
            size="flexible"
            className="rounded"
            borderColor={tailwindColors.grey.light.DEFAULT}
            color={tailwindColors.white.active}
            text={
              <div className="flex items-center gap-1  ">
                <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                  {t(
                    'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.monday.thursday'
                  )}
                </Typography>
                <Badge variant="secondaryRed" text="09:00" />
                <Badge variant="secondaryRed" text="18:00" />
              </div>
            }
          />
          <Badge
            size="flexible"
            className="rounded"
            borderColor={tailwindColors.grey.light.DEFAULT}
            color={tailwindColors.white.active}
            text={
              <div className="flex items-center gap-1">
                <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                  {t(
                    'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.friday.saturday'
                  )}
                </Typography>
                <Badge variant="secondaryRed" text="09:00" />
                <Badge variant="secondaryRed" text="17:00" />
              </div>
            }
          />
        </div>
      </InnerBox>
      <InnerBox className="rounded-lg p-3 bg-white-hover">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          {t(
            'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.FAC.numbers.title'
          )}
        </Typography>
        <div className="h-2" />
        <Typography variant="tag" color={tailwindColors.grey.lighter}>
          {t(
            'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.FAC.numbers.text'
          )}
        </Typography>
        <div className="h-4" />
        <div className="flex flex-col gap-4 ">
          {groupedData?.map((staff, index) => (
            <div
              key={index}
              className="flex items-center gap-2 mobile:flex-col mobile:items-start"
            >
              <Typography variant="secondary-body" color={tailwindColors.secondary.DEFAULT}>
                {formatMonthDisplay(staff.month)} :
              </Typography>

              <div className="flex flex-wrap items-center gap-1 mobile:flex-col mobile:items-start">
                {staff.facStaffs.map((facStaff) => (
                  <Badge
                    key={facStaff.id}
                    size="flexible"
                    className="rounded"
                    color={tailwindColors.white.active}
                    borderColor={tailwindColors.grey.light.DEFAULT}
                    text={
                      <div className="flex items-center gap-1">
                        {facStaff.staffRequest.startDate && facStaff.staffRequest.endDate && (
                          <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                            {`${DateFormatter.DDMM(facStaff.staffRequest.startDate)} - ${DateFormatter.DDMM(facStaff.staffRequest.endDate)}`}
                          </Typography>
                        )}
                        <Badge
                          variant="primary"
                          text={`${toFullName(facStaff.staff.firstName, facStaff.staff.lastName)} ${facStaff.facNumber ? formatPhoneNumber(facStaff.facNumber) : ''}`}
                        />
                      </div>
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </InnerBox>

      <InnerBox className="rounded-lg p-3 bg-white-hover">
        <div className="flex items-center gap-2.5 mobile:flex-col mobile:items-start">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            {t(
              'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.journey'
            )}
          </Typography>
          <Badge variant="blue" text="+33 (0)1 42 67 00 00" />
        </div>
        <div className="h-2" />
        <Typography variant="tag" color={tailwindColors.grey.lighter}>
          {t(
            'staff.personal.space.my.journeys.details.tab.home.urgent.number.modal.lec.journey.text'
          )}
        </Typography>
      </InnerBox>
    </div>
  )
}

export default AffectedStaffUrgentNumberModal
